// This is adapted from "@rails/activestorage/src/file_checksum" for Shopify

import SparkMD5 from 'spark-md5';

const fileSlice = File.prototype.slice as (
  this: File,
  start: number,
  end: number,
  contentType?: string,
) => Blob;

type ChecksumCallback = (error: string | null, checksum?: string) => void;

export class FileChecksum {
  file: File;
  chunkSize: number;
  chunkCount: number;
  chunkIndex: number;
  callback?: ChecksumCallback;
  fileReader!: FileReader;
  md5Buffer!: SparkMD5.ArrayBuffer;

  static create(file: File, callback: ChecksumCallback): void {
    const instance = new FileChecksum(file);
    instance.create(callback);
  }

  constructor(file: File) {
    this.file = file;
    this.chunkSize = 2097152;
    this.chunkCount = Math.ceil(this.file.size / this.chunkSize);
    this.chunkIndex = 0;
  }

  create(callback: ChecksumCallback): void {
    this.callback = callback;
    this.md5Buffer = new SparkMD5.ArrayBuffer();
    this.fileReader = new FileReader();
    this.fileReader.addEventListener(
      'load',
      (event: ProgressEvent<FileReader>) => this.fileReaderDidLoad(event),
    );
    this.fileReader.addEventListener('error', () => this.fileReaderDidError());
    this.readNextChunk();
  }

  fileReaderDidLoad(event: ProgressEvent<FileReader>): void {
    const target = event.target;
    if (target && target.result instanceof ArrayBuffer) {
      this.md5Buffer.append(target.result);
    }

    if (!this.readNextChunk()) {
      const binaryDigest: string = this.md5Buffer.end(true);
      const base64digest: string = btoa(binaryDigest);
      if (this.callback) {
        this.callback(null, base64digest);
      }
    }
  }

  fileReaderDidError(): void {
    if (this.callback) {
      this.callback(`Error reading ${this.file.name}`);
    }
  }

  readNextChunk(): boolean {
    if (
      this.chunkIndex < this.chunkCount ||
      (this.chunkIndex === 0 && this.chunkCount === 0)
    ) {
      const start = this.chunkIndex * this.chunkSize;
      const end = Math.min(start + this.chunkSize, this.file.size);
      const bytes = fileSlice.call(this.file, start, end);
      this.fileReader.readAsArrayBuffer(bytes);
      this.chunkIndex++;
      return true;
    } else {
      return false;
    }
  }
}
